import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API from "@/utils/api";
import RiderLayout from "./RiderLayout";
import { Wallet, TrendingUp, Clock, ChevronRight, Building2, Landmark, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function RiderWallet() {
    const [stats, setStats] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem("rider_token");
            if (!token) return;

            const [statsRes, historyRes] = await Promise.all([
                axios.get(`${API}/rider/stats/earnings`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/rider/history?limit=10`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            setStats(statsRes.data);
            setHistory(historyRes.data);
        } catch (error) {
            console.error("Error fetching wallet data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSettle = async (companyId, restaurantName) => {
        if (!window.confirm(`¿Confirmas que has recibido el pago de ${restaurantName}?`)) return;

        try {
            const token = localStorage.getItem("rider_token");
            await axios.post(`${API}/rider/stats/settle/${companyId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Liquidación completada con ${restaurantName}`);
            fetchData();
        } catch (error) {
            toast.error("Error al procesar la liquidación");
        }
    };

    if (loading) {
        return (
            <RiderLayout>
                <div className="flex flex-col items-center justify-center h-64 animate-pulse">
                    <Wallet size={48} className="text-slate-800 mb-4" />
                    <div className="h-4 w-32 bg-slate-800 rounded mb-2" />
                    <div className="h-4 w-48 bg-slate-900 rounded" />
                </div>
            </RiderLayout>
        );
    }

    return (
        <RiderLayout>
            <div className="p-4 space-y-6 pb-24">
                {/* Balance Card */}
                <div className="bg-gradient-to-br from-primary to-blue-700 rounded-[32px] p-6 shadow-[0_20px_40px_rgba(59,130,246,0.3)] relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 blur-[60px] rounded-full" />
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-black/10 blur-[60px] rounded-full" />

                    <div className="relative z-10">
                        <span className="text-blue-100 text-[10px] font-black uppercase tracking-widest opacity-80 decoration-primary decoration-2 underline-offset-4 underline">Pdte. de Cobro</span>
                        <div className="flex items-end gap-1 mt-1">
                            <span className="text-4xl font-black text-white">{stats?.pending_total?.toFixed(2) || "0.00"}</span>
                            <span className="text-xl font-bold text-blue-200 mb-1">{stats?.symbol || "€"}</span>
                        </div>
                        <p className="text-blue-100/60 text-[10px] mt-2 font-medium">Lunes - Domingo (Semana Actual)</p>

                        <div className="mt-6 flex gap-4">
                            <div className="bg-white/20 backdrop-blur-md rounded-2xl p-3 flex-1 border border-white/10">
                                <span className="text-[9px] text-white/70 block uppercase font-black">Ganado esta semana</span>
                                <span className="text-sm font-black text-white">{stats?.weekly_total?.toFixed(2)}{stats?.symbol}</span>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 flex-1 border border-white/5">
                                <span className="text-[9px] text-white/70 block uppercase font-black">Histórico Total</span>
                                <span className="text-sm font-black text-white">{stats?.total_lifetime?.toFixed(2)}{stats?.symbol}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Earnings by Restaurant */}
                <div className="space-y-3">
                    <div className="flex justify-between items-center px-1">
                        <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                            <Building2 size={14} className="text-primary" />
                            Pendiente por Restaurante
                        </h2>
                    </div>

                    {!stats?.by_restaurant || stats.by_restaurant.length === 0 ? (
                        <div className="bg-white/5 border border-white/5 rounded-2xl p-6 text-center">
                            <Landmark size={24} className="mx-auto text-slate-700 mb-2" />
                            <p className="text-xs text-slate-500 font-medium">No tienes pagos pendientes de cobro. ¡Buen trabajo!</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {stats.by_restaurant.map((item) => (
                                <div key={item.company_id} className="bg-white/5 border border-white/10 rounded-[28px] p-4 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                                                <Building2 size={20} className="text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-black text-white leading-tight">{item.restaurant_name}</p>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase">{item.count} {item.count === 1 ? 'pedido' : 'pedidos'}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-black text-green-400">{item.earnings.toFixed(2)}{stats.symbol}</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleSettle(item.company_id, item.restaurant_name)}
                                        className="w-full py-2.5 bg-white/5 hover:bg-green-500/20 text-green-500 border border-green-500/30 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                    >
                                        Confirmar Cobro <ChevronRight size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Recent Activity */}
                <div className="space-y-3">
                    <div className="flex justify-between items-center mb-4 px-1">
                        <h2 className="text-xs font-black uppercase tracking-widest text-slate-400">Actividad Reciente</h2>
                        <Link to="/rider/history" className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline">Ver todo</Link>
                    </div>

                    <div className="space-y-2">
                        {history.length === 0 ? (
                            <div className="text-center py-8 bg-white/5 border border-dashed border-white/10 rounded-3xl">
                                <p className="text-xs text-slate-500 font-medium">No hay entregas registradas aún.</p>
                            </div>
                        ) : (
                            history.map((job) => (
                                <div key={job.job_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex justify-between items-center">
                                    <div className="flex gap-3 items-center">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${job.status === 'delivered' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                            {job.status === 'delivered' ? <TrendingUp size={20} /> : <Clock size={20} />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-black text-white leading-tight">
                                                    {job.status === 'delivered' ? 'Entrega Exitosa' : 'Cancelado'}
                                                </p>
                                                {job.rider_paid && (
                                                    <span className="text-[8px] bg-green-500/20 text-green-500 px-1.5 py-0.5 rounded-full font-black uppercase">Cobrado</span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter truncate w-32">
                                                {job.restaurant_name} • #{job.job_id.slice(-6).toUpperCase()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-sm font-black ${job.status === 'delivered' ? 'text-white' : 'text-slate-500 line-through'}`}>
                                            +{(job.rider_earnings + job.tip_amount + job.bonus_amount).toFixed(2)}{stats?.symbol || "€"}
                                        </span>
                                        <p className="text-[9px] text-slate-500 font-bold mt-1">
                                            {new Date(job.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Info Footer */}
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4">
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0">
                            <Landmark size={14} className="text-yellow-500" />
                        </div>
                        <p className="text-[10px] text-yellow-200/80 leading-relaxed font-medium">
                            <span className="font-black text-yellow-500 uppercase block mb-1">Doble Validación de Cobro</span>
                            Al confirmar un cobro, el restaurante verá que has recibido el dinero de estos pedidos. Realiza esta acción solo cuando tengas el pago en mano.
                        </p>
                    </div>
                </div>
            </div>
        </RiderLayout>
    );
}
